import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Navigation, Router } from "@angular/router";
import { interval, of, Subscription } from "rxjs";
import { catchError, switchMap, takeWhile, filter } from "rxjs/operators";
import { PaymentTransactionService } from "src/app/services/payment/payment-transaction.service";
import { NgZone } from "@angular/core";
import { NotificationService } from "src/app/services/notification.service";

interface PaymentState {
  intentLink: string;
  txId?: string;
  amount: number;
  message?: string;
  formData: { numberOfUnits: string };
  bidPrice: number;
  energyAmount: number;
  transactionType: number;
  customerId: number;
}

@Component({
  selector: "app-web-payment-model",
  templateUrl: "./web-payment-model.component.html",
  styleUrls: ["./web-payment-model.component.scss"],
})
export class WebPaymentModelComponent implements OnInit, OnDestroy {
  intentLink!: string;
  txId!: string;
  amount!: number;
  message?: string;
  returnRoute!: string; // Where to navigate on completion

  displayTitle: string = "Payment Request";
  date!: string;
  state: "waiting" | "success" | "failed" = "waiting";

  // --- Optional Properties (Primarily for Bidding Flow) ---
  units?: number;

  // --- Private Properties ---
  private fullPaymentContext: any; // Stores the entire context for the return trip
  private timerSub!: Subscription;
  private pollSub!: Subscription;
  private notifSub!: Subscription;
  showQr = false;
  safeQrSrc!: SafeUrl;
  timeLeft = 300;
  qr_code:any

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    private paymentTx: PaymentTransactionService,
    private notifications: NotificationService,
    private zone: NgZone
  ) {
    const nav: Navigation | null = this.router.getCurrentNavigation();
    let data: any = nav?.extras.state ?? {};

    if (!data.intentLink) {
      const storedContext = sessionStorage.getItem("pendingPaymentContext");
      if (storedContext) {
        data = JSON.parse(storedContext);
      } else {
        console.error("Payment context not found. Navigating away.");
        this.router.navigate(["/market"]);
        return; // Stop execution if there's no data
      }
    }

    this.fullPaymentContext = data;
    this.qr_code = data.qr_code
    this.intentLink = data.intentLink;
    this.amount = data.amount;
    this.message = data.message;
    this.returnRoute = data.returnRoute; // This is new and crucial
    this.displayTitle = data.display?.title || "Payment Request Details";

    if (data.formData?.numberOfUnits) {
      this.units = parseFloat(data.formData.numberOfUnits);
    }

    const parsed = new URL(
      this.intentLink.replace("DigitalRupee://pay?", "https://dummy?")
    );
    //this.txId = data.txId ?? parsed.searchParams.get('tr')!;
    this.txId = parsed.searchParams.get("tr")!;

    const now = new Date();
    this.date = now.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  ngOnInit(): void {
    this.timerSub = interval(1000)
      .pipe(takeWhile(() => this.timeLeft >= 0))
      .subscribe(() => {
        if (this.timeLeft == 0) this.onFinished(false, true);
        else {
          this.timeLeft--;
        }
      });

    this.pollSub = interval(10000)
      .pipe(
        switchMap(() =>
          this.paymentTx
            .getTransactionStatus(this.txId)
            .pipe(catchError(() => of(null)))
        )
      )
      .subscribe((tx: any) => {
        if (!tx) return;
        const st = (tx.status ?? "").toString().toUpperCase();
        if (["COMPLETED"].includes(st)) {
          this.onFinished(true);
        }
      });

    this.notifSub = this.notifications.connect().subscribe((notif) => {
      console.log("Got notification:", notif);
      console.log("Type:", notif.type);
      console.log("Reference:", notif.reference);
      if (notif.type === "payment" && notif.reference === this.txId) {
        this.zone.run(() => this.onFinished(true));
      }
    });
  }

  ngOnDestroy(): void {
    this.timerSub?.unsubscribe();
    this.pollSub?.unsubscribe();
    this.notifications.disconnect();
    this.notifSub?.unsubscribe();
    sessionStorage.removeItem("pendingPaymentContext");
    sessionStorage.removeItem("pendingBidContext");
  }

  private onFinished(success: boolean, timeout = false): void {
    this.timerSub.unsubscribe();
    this.pollSub.unsubscribe();
    sessionStorage.removeItem("pendingPaymentContext");
    sessionStorage.removeItem("pendingBidContext");

    if (success) {
      // If the original context was for a bid (it had formData), we return the whole object.
      // Otherwise (for Buy Tokens), we just return a simple success flag.
      const isComplexFlow =
        this.fullPaymentContext.formData ||
        this.fullPaymentContext.editContext ||
        this.fullPaymentContext.acceptanceContext ||
        this.fullPaymentContext.preferentialEditContext ||
        this.fullPaymentContext.preferentialAcceptContext;

      const stateForNav = isComplexFlow
        ? { ...this.fullPaymentContext, paymentResult: "success" }
        : { paymentResult: "success" };

      this.zone.run(() => {
        // We use the 'returnRoute' property that was passed in the context.
        this.router.navigate([this.returnRoute], {
          state: stateForNav,
          replaceUrl: true,
        });
      });
    } else {
      // immediate return on timeout or failure
      this.state = "failed";
      this.message = timeout
        ? "Payment timed out. Redirecting back…"
        : "Payment failed. Redirecting back…";
      // give the user a quick moment to read the message
      setTimeout(() => {
        this.zone.run(() => {
          this.router.navigate([this.returnRoute || "/market"], {
            replaceUrl: true,
          });
        });
      }, 1000);
    }
  }

  toggleQr(): void {
    if (this.showQr) return;
    this.showQr = true;
    const base64WithPrefix = `data:image/png;base64,${this.qr_code}`;
    // const payload = encodeURIComponent(this.intentLink);
    // const url = `https://api.qrserver.com/v1/create-qr-code/?data=${payload}&size=250x250`;
    this.safeQrSrc = this.sanitizer.bypassSecurityTrustUrl(base64WithPrefix);
  }

  cancel(): void {
    sessionStorage.removeItem("pendingPaymentContext");
    sessionStorage.removeItem("pendingBidContext");
    this.router.navigate([this.returnRoute || "/market"], { replaceUrl: true });
  }

  get timeString(): string {
    
    const safeTime = Math.max(0, this.timeLeft);

    const m = Math.floor(safeTime / 60)
      .toString()
      .padStart(2, "0");
    const s = (safeTime % 60).toString().padStart(2, "0");

    return `${m}:${s}`;
  }

  get progressStyle(): any {
    const pct = (this.timeLeft / 300) * 100;

    return {
      // background: `conic-gradient(#008080 ${pct}%, #E0E0E0 ${pct}% 100%)`,
    };
  }
}
